/**
 * This file holds computed state properties
 * Please refer to https://vuex.vuejs.org/guide/getters.html for more information
 */

 import addUrlParams from "@/http/addUrlParams";

 const getters = {
   getUrlParameterByName() {
     return name => {
       const url = window.location.href;
       name = name.replace(/\[\]/g, "\\$&");
       const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)", "i");
       const results = regex.exec(url);

       if (!results) return null;
       if (!results[2]) return "";
       return decodeURIComponent(results[2].replace(/\+/g, " "));
     };
   },
   /**
    * Returns true only if the activeComponent is GCC. It doesn't check all the components
    */
   isGCC(state, getters, rootState) {
     // if the URL contains a query parameter with submission id, then it's GCC
     const submission_id = getters.getUrlParameterByName("submission_id");
     if (submission_id) {
       return true;
     }
     // if the active component is UGC and GCC, then it's GCC
     const { activeUGCComponent } = rootState.ugc.tempData.features[rootState.ugc.tempData.activeUGCIndex];
     const { components = null } = rootState.recipe.recipe_v2;
     const component = components ? components[activeUGCComponent] : null;
     const recipeHasGCC = (component && component.ugc && component.ugc.gcc) || false;
     return recipeHasGCC;
   },
   returnScreenSize(state) {
     if (state.windowWidth < state.screenSizes.mobile5) {
       return "mobile5";
     } else if (state.windowWidth < state.screenSizes.mobileX) {
       return "mobileX";
     } else if (state.windowWidth < state.screenSizes.mobilePlus) {
       return "mobilePlus";
     } else if (state.windowWidth < state.screenSizes.medium) {
       return "medium";
     } else {
       return "large";
     }
   },
   returnSpinnerSize(state, getters) {
     const screenSize = getters["returnScreenSize"];
     if (screenSize === "small") {
       return "40";
     } else if (screenSize === "medium") {
       return "50";
     } else {
       return "60";
     }
   },
   availableSwatchesByComponent(state, getters, rootState) {
     let swatches = [];
     if (!state.pdm || !state.availableSwatches.length) return swatches;
     const { activeUGCComponent } = rootState.ugc.tempData.features[rootState.ugc.tempData.activeUGCIndex];
     const { components } = state.pdm;
     const hasUGC =
       components[activeUGCComponent] && components[activeUGCComponent].ugc;
     const { available_checkerboard_swatches } = state.availablePatternMakerContent[state.pdm.sku]
        && state.availablePatternMakerContent[state.pdm.sku][activeUGCComponent];
     const swatchesEnabled =
       hasUGC &&
       Array.isArray(available_checkerboard_swatches);
     const available_swatches = swatchesEnabled
       ? available_checkerboard_swatches
       : [];
     if (!activeUGCComponent || !swatchesEnabled || !available_swatches.length)
       return swatches;
     swatches = state.availableSwatches
       .filter(({ id }) => available_swatches.includes(Number(id)))
       .sort((a, b) => available_swatches.indexOf(parseInt(a.id)) - available_swatches.indexOf(parseInt(b.id)));
     return swatches;
   },
   availableStickersByComponent(state, getters, rootState) {
     let stickers = [];
     if (!state.pdm || !state.availableStickers.length) return stickers;
     const { activeUGCComponent } = rootState.ugc.tempData.features[rootState.ugc.tempData.activeUGCIndex];
     const { components } = state.pdm;
     const { available_stickers } = state.availablePatternMakerContent[state.pdm.sku]
      && state.availablePatternMakerContent[state.pdm.sku][activeUGCComponent];
     const hasUGC = components[activeUGCComponent] && components[activeUGCComponent].ugc;
     const stickersEnabled =   hasUGC && Array.isArray(available_stickers);
     const available_stickers_data = stickersEnabled
       ? available_stickers
       : [];
     if (!activeUGCComponent || !stickersEnabled || !available_stickers_data.length)
       return stickers;
     stickers = state.availableStickers.
       filter(({ id }) => available_stickers.includes(Number(id)));
     return stickers;
   },
   availableStickersOrderByComponent(state, getters, rootState) {
     if (!state.pdm) {
       return [];
     }
     const { activeUGCComponent } = rootState.ugc.tempData.features[rootState.ugc.tempData.activeUGCIndex];
     const { components } = state.pdm;
     const { available_stickers } = state.availablePatternMakerContent[state.pdm.sku]
     && state.availablePatternMakerContent[state.pdm.sku][activeUGCComponent];
     const hasUGC = components[activeUGCComponent] && components[activeUGCComponent].ugc;
     const stickersEnabled = hasUGC &&  Array.isArray(available_stickers);
     const stickers_order = stickersEnabled
       ? available_stickers
       : [];
     return stickers_order;
   },
   getComponentsUgc() {
     return ({ recipe }) => Object.keys(recipe.components).filter(item => recipe.components[item].hasOwnProperty("ugc"));
   },
   addCacheBusting(state) {
     return  url => {
       const version = (state.configuration && state.configuration.version) || process.env.VERSION;
       return addUrlParams(  url, { v: version } );
     };
   },
   isValidEmail() {
     return ({ email }) => {
       const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/;
       return regex.test(email);
     };
   },
   regionIsApac(state) {
     return state.configuration && (state.configuration.locale === 'zh_cn' || state.configuration.locale === 'ko_kr');
   },
   tmallEnabled(state) {
     return state.configuration && state.configuration.integrations.tmall.enabled;
   },
   noEsiActive(state) {
     return !!state.queryParams['no-esi'] || window.location.href.includes('/2.0/customizer') || state.queryParams.canvas;
   },
   vsfActive(state) {
     return !!state.queryParams['vsf'] || state.vsfActive;
   },
   canvasActive(state) {
     return !!state.queryParams['canvas']; 
   },
   /**
    * Checks if apparel is enabled in the configurations and if the model is
    * part of the list of enabled apparel models
    */
   isApparelEnabled(state) {
     // checks if apparel is enabled in the configurations or via query param
     const enabledFlagOrParam = !!(
       state.queryParams["isApparel"] ||
       state?.configuration?.apparel_settings?.enable_apparel
     );
     const pathName = window.location.pathname;
     const splitPath = pathName.split('.');
     let pdmModel = state?.queryParams?.model || splitPath[1];
     // checks if the model is part of the list with apparel enabled
     const apparelEnabledModels = state?.configuration?.apparel_settings?.models_enabled;
     const modelIsEnabled = !!(
       apparelEnabledModels && apparelEnabledModels.includes(pdmModel)
     );
     // returns true if only both conditions are met
     return enabledFlagOrParam && modelIsEnabled;
   },
   /**
    * Checks if the Ecommerce 1.5 Data Layer Format should be used.
    *
    * @param state the store state
    * @param getters the getters for this store module
    * @returns {Boolean} true if the Ecommerce 1.5 Data Layer Format should be used.
    */
   useEcomDataLayerFormat(state, getters) {
     return getters.noEsiActive || getters.vsfActive || getters.canvasActive;
   },
   replaceEmailToHtml() {
     return ({ text }) => {
       if (text && typeof text === 'string') {
         const emailsMatch = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi
         return text.replace(emailsMatch, `<a href="mailto:$1">$1</a>`);
       }
       return '';
     }
   },
   isUGCenabled(state){
       if (!state.pdm || !state.configuration || state.configuration.ugc.turn_off_all_models) return false;
       return state.configuration.ugc.models_enabled[state.pdm.sku] ? true : false;
   },
   isComponentUGCenabled(state){
     return ({ componentName }) => {
       if (!state.pdm || !state.configuration || state.configuration.ugc.turn_off_all_models) return false;
       return state.configuration.ugc.models_enabled[state.pdm.sku].components.includes(componentName);
     }
   },
   isPersonalizationEnabled(state){
     if (!state.pdm || !state.configuration) return false;
     if (state.configuration.personalization.turn_off_all_models) return false;
     return state.configuration.personalization.models_enabled.includes(state.pdm.sku);
   },
   baseUrl(store, getters, rootState) {
     return ( type ) => {
       const s3 = rootState.endPoints[type].s3Url || '';
       let locale = rootState.utils.configuration.locale.toLowerCase() || '';

       if (locale) {
         // Check locale with dash
         locale = locale.replace('_', '-');
         let s3WithLocale = `${ s3 }/${ locale }`;
         if ((!!s3 || !!locale) && window.location.href.includes(s3WithLocale)) return s3WithLocale;

         // Check locale with underscore
         locale = locale.replace('-', '_');
         s3WithLocale = `${ s3 }/${ locale }`;
         if ((!!s3 || !!locale) && window.location.href.includes(s3WithLocale)) return s3WithLocale;
       }

       return s3;
     };
   },
   isIframeSameOrigin() {
     let isIframeSameOrigin;
     try {
       isIframeSameOrigin = window.parent.location.href !== window.location.href;
     } catch {
       isIframeSameOrigin = false;
     }
     return isIframeSameOrigin;
   },
   /**
    * Returns an array of the names of all the components that have graphics support.
    */
   componentsWithGraphicsSupport(state) {
     const components = state?.pdm?.components
     return components ? Object.keys(state?.pdm?.components).filter(
       (item) => state?.pdm?.components?.[item]?.graphicspackage === true
     ) : []
   },
  isMobileAndSafari(state, getters) {
    const isMobile = getters['returnScreenSize'].includes('mobile');
    return isMobile && navigator.userAgent.toLowerCase().includes('safari');
  },
  isComponentLibraryEnabled(state){
    return ({ componentName }) => {
      if (!state.pdm || !state.configuration || state.configuration.image_library.turn_off_all_models) return false;
      return state.configuration.image_library?.models_enabled?.[state.pdm.sku].components.includes(componentName);
    }
  },
  isImageLibraryEnabled(state){
    if (!state.pdm || !state.configuration || state.configuration.image_library.turn_off_all_models) return false;
    return state.configuration.image_library?.models_enabled?.[state.pdm.sku] ? true : false;
   },
 };

 export default getters;